import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "./icons";

import { cn } from "utils/cn";
import { useFormFieldControl } from "./FormField";
import { controlClasses } from "./Input";
import { popoverSurfaceClasses } from "./Popover";

export const SelectRoot = SelectPrimitive.Root;

export interface SelectTriggerProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, "placeholder"> {
  placeholder?: React.ReactNode;
  /** Sets aria-required; native validation comes from `required` on SelectRoot. */
  required?: boolean;
}

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, placeholder, ...rest }, ref) => {
  const { required, ...props } = useFormFieldControl(rest);
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      data-ui="select"
      aria-required={required || undefined}
      className={cn(
        controlClasses,
        "flex h-10 items-center justify-between gap-2 px-3 text-left",
        "data-[placeholder]:text-subtle-foreground",
        "data-[state=open]:border-primary data-[state=open]:ring-4 data-[state=open]:ring-ring/15",
        className
      )}
      {...props}
    >
      <SelectPrimitive.Value placeholder={placeholder} className="min-w-0 truncate" />
      <SelectPrimitive.Icon asChild>
        <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = "SelectTrigger";

const scrollButtonClasses =
  "flex cursor-default items-center justify-center py-1 text-muted-foreground";

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", sideOffset = 6, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={sideOffset}
      className={cn(
        popoverSurfaceClasses,
        "relative overflow-hidden origin-[--radix-select-content-transform-origin]",
        position === "popper" &&
          "max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)]",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ScrollUpButton className={scrollButtonClasses}>
        <ChevronUp aria-hidden="true" className="h-4 w-4" />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className={scrollButtonClasses}>
        <ChevronDown aria-hidden="true" className="h-4 w-4" />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = "SelectContent";

export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  /** Secondary line under the label; not shown in the trigger. */
  description?: React.ReactNode;
}

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, description, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none flex-col rounded-control py-1.5 pl-2 pr-8 text-sm text-foreground outline-none",
      "transition-colors duration-fast ease-standard data-[highlighted]:bg-surface-muted",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {description && <span className="text-xs text-muted-foreground">{description}</span>}
    <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center text-primary-text">
      <SelectPrimitive.ItemIndicator>
        <Check aria-hidden="true" className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = "SelectItem";

export interface SelectOption<V extends string = string> {
  value: V;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> {
  options: SelectOption<V>[];
  /** "" shows the placeholder. */
  value?: V | "";
  defaultValue?: V;
  onValueChange?: (value: V) => void;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  /** Layout classes for the trigger. */
  className?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

const SelectInner = <V extends string = string>(
  {
    options,
    value,
    defaultValue,
    onValueChange,
    placeholder,
    disabled,
    required,
    name,
    ...triggerProps
  }: SelectProps<V>,
  ref: React.ForwardedRef<HTMLButtonElement>
) => (
  <SelectRoot
    value={value}
    defaultValue={defaultValue}
    onValueChange={onValueChange && ((next: string) => onValueChange(next as V))}
    disabled={disabled}
    required={required}
    name={name}
  >
    <SelectTrigger ref={ref} placeholder={placeholder} required={required} {...triggerProps} />
    <SelectContent>
      {options.map((option) => (
        <SelectItem
          key={option.value}
          value={option.value}
          description={option.description}
          disabled={option.disabled}
        >
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </SelectRoot>
);

const SelectWithRef = React.forwardRef(SelectInner);
SelectWithRef.displayName = "Select";

/** Single-value select over a typed option list; works inside FormField. */
export const Select = SelectWithRef as <V extends string = string>(
  props: SelectProps<V> & { ref?: React.Ref<HTMLButtonElement> }
) => React.ReactElement | null;
